<script>
import { mapState } from 'vuex'
import date from 'locutus/php/datetime/date'
import long2ip from 'locutus/php/network/long2ip'
import PageNavigation from '@components/admin/page-navigation'
import AccountInfo from '@components/admin/account-info'
import {
  fetchList,
  fetchAccountAddress,
  setAccountFraud,
  setAccountOpen,
} from '@apis/admin/account'
import { fetchAssets } from '@apis/admin/asset'
import fromPairs from 'lodash/fromPairs'
import map from 'lodash/map'
import mapKeys from 'lodash/mapKeys'
import pick from 'lodash/pick'

export default {
  components: { PageNavigation, AccountInfo },
  data() {
    return {
      loading: false,
      list: [],
      assets: [],
      total_count: 0,
      page: 1,
      total_page: 0,
      page_navigation: {},
      search_params: {},
      show_account_info: false,
      target_account_seq: 0,
      excel_headers: [
        ['seq', this.$t('account_list.seq')],
        ['member_seq', this.$t('account_list.member_seq')],
        ['address_seq', this.$t('account_list.address_seq')],
        ['user_id', this.$t('account_list.user_id')],
        ['user_name', this.$t('account_list.user_name')],
        ['email_address', this.$t('account_list.email_address')],
        ['title', this.$t('account_list.title')],
        ['account_number', this.$t('account_list.account_number')],
        ['balance', this.$t('account_list.balance')],
        ['status', this.$t('account_list.status')],
        ['regdate', this.$t('account_list.regdate')],
        ['address', this.$t('account_list.address')],
        ['asset_title', this.$t('account_list.asset_title')],
        ['asset_code', this.$t('account_list.asset_code')],
        ['holding_balance', this.$t('account_list.holding_balance')],
      ],
    }
  },
  computed: {
    ...mapState('common', {
      constants: (state) => state.constants,
    }),
  },
  watch: {
    $route(to, from) {
      this.page = this.$route.query.page || 1
      this.loadList().catch((e) => this.$toasted.error(e.message))
    },
  },
  created() {
    this.init()
  },
  methods: {
    init() {
      this.page = this.$route.query.page || 1
      this.search_params = { ...this.$route.query }
      Promise.all([this.loadList(), this.loadAssets()])
        .catch((e) => this.$toasted.error(e.message))
        .finally(() => {
          this.loading = false
        })
    },
    async loadAssets() {
      const { data } = await fetchAssets()
      this.assets = data
    },
    async loadList() {
      this.loading = true
      const ret_obj = await fetchList({
        ...this.$route.params,
        ...this.$route.query,
      })

      this.list = ret_obj.data
      this.total_count = ret_obj.total_count
      this.total_page = ret_obj.total_page
      this.page_navigation = ret_obj.page_navigation
    },
    search() {
      this.$router.push({ query: this.search_params })
    },
    clearAndSearch() {
      this.search_params = pick(this.search_params, 'market_seq')
      this.search()
    },
    date,
    fromPairs,
    long2ip,
    handleCreateAccount() {
      this.show_account_info = true
      this.target_account_seq = null
    },
    handleUpdateAccount({ seq }) {
      this.show_account_info = true
      this.target_account_seq = seq
    },
    handleAccountSubmit(seq) {
      this.init()
      this.target_account_seq = seq
    },
    handleCopyPrivateKey(address_seq) {
      fetchAccountAddress(address_seq)
        .then(({ address_info }) => {
          if (!address_info) return

          return this.$copyText(address_info.private_key)
        })
        .then(() => this.$toasted.success(this.$t('toasted_msg.copy')))
        .catch((e) => this.$toasted.error(e.message))
    },
    async handleExportExcel() {
      this.loading = true
      try {
        if (
          !(await this.$confirm(
            this.$t('admin.msg_excel_warning'),
            this.$t('admin.modal_confirm'),
            {
              type: 'warning',
            }
          ).catch(() => false))
        )
          return

        const { data } = await fetchList({
          ...this.$route.params,
          ...this.$route.query,
          no_paging: 1,
        })

        const { default: exportJsonToExcel } = await import(
          '@utils/export-json-to-excel'
        )
        exportJsonToExcel({
          header: map(this.excel_headers, ([, title]) => title),
          data: this.format2excel(data),
          filename: this.$t('account_list.account_history', [date('YmdHis')]),
        })
      } catch (e) {
        this.$toasted.error(e.message)
      } finally {
        this.loading = false
      }
    },
    format2excel(list) {
      const excel_fields = map(this.excel_headers, ([name]) => name)
      return map(list, (data) => {
        let result = pick(data, excel_fields)
        const keys = Object.keys(result)
        result = mapKeys(
          map(result, (val, key) => {
            switch (key) {
              case 'regdate':
                return val ? date('Y-m-d H:i:s', new Date(val)) : ''
              default:
                return val
            }
          }),
          (val, key) => {
            return keys[key]
          }
        )

        return map(excel_fields, (key) => result[key])
      })
    },
    handleAccountOpen(seq) {
      this.loading = true
      this.$confirm(
        this.$t('admin.msg_accountopen'),
        this.$t('admin.modal_confirm'),
        {
          type: 'warning',
        }
      )
        .then(() =>
          setAccountOpen({
            account_seq: seq,
          })
        )
        .then(({ seq }) => {
          this.$emit('submit', seq)
        })
        .then(() =>
          this.$toasted.success(this.$t('admin.msg_accountopen_success'))
        )
        .then(() => this.init())
        .catch((e) => (e ? this.$toasted.error(e.message) : null))
        .finally(() => {
          this.loading = false
        })
    },
    handleAccountFraud(seq) {
      this.loading = true
      this.$confirm(
        this.$t('admin.msg_accountfraud'),
        this.$t('admin.modal_confirm'),
        {
          type: 'warning',
        }
      )
        .then(() =>
          setAccountFraud({
            account_seq: seq,
          })
        )
        .then(({ seq }) => {
          this.$emit('submit', seq)
        })
        .then(() =>
          this.$toasted.success(this.$t('admin.msg_accountfraud_success'))
        )
        .then(() => this.init())
        .catch((e) => (e ? this.$toasted.error(e.message) : null))
        .finally(() => {
          this.loading = false
        })
    },
  },
}
</script>

<template>
  <div v-loading="loading">
    <ElDialog
      :title="
        target_account_seq
          ? $t('admin.account_info')
          : $t('admin.account_enroll')
      "
      :visible.sync="show_account_info"
    >
      <AccountInfo
        v-if="show_account_info"
        :key="target_account_seq"
        :account_seq="target_account_seq"
        @submit="handleAccountSubmit"
      ></AccountInfo>
    </ElDialog>

    <h3 :class="$style.xeAdmin">{{ $t('admin.search') }}</h3>
    <form @submit.prevent="search">
      <table cellspacing="0" cellpadding="0" :class="$style.rowTable">
        <colgroup>
          <col width="20%" />
          <col width="30%" />
          <col width="20%" />
          <col width="30%" />
        </colgroup>
        <tbody>
          <tr>
            <th scope="col">
              <div>
                <select
                  v-model="search_params.search_target"
                  :class="$style.fullWidth"
                >
                  <option
                    v-for="val in [
                      [undefined, $t('admin.search_target')],
                      ['user_id', $t('admin.user_id')],
                      ['account_number', $t('wallet.account_number')],
                      ['address', $t('admin.deposit_address')],
                      ['email_address', $t('admin.email_address')],
                      ['user_name', $t('admin.user_name')],
                      ['regdate_more', $t('admin.login_log_regdate_more')],
                      ['regdate_less', $t('admin.login_log_regdate_less')],
                    ]"
                    :key="val[1]"
                    :value="val[0]"
                    >{{ val[1] }}</option
                  >
                </select>
              </div>
            </th>
            <td>
              <input
                v-model="search_params.search_keyword"
                :placeholder="$t('admin.search_word')"
                type="text"
                :class="[$style.inputTypeText, $style.fullWidth]"
              />
            </td>
            <td colspan="2">
              <ElCheckbox
                v-model="search_params.use_statistics_exclude"
                :false-label="undefined"
                true-label="N"
                >{{ $t('admin.use_statistics_exclude') }}</ElCheckbox
              >
              <ElCheckbox
                v-model="search_params.member_is_alive"
                :false-label="undefined"
                true-label="Y"
                >{{ $t('admin.member_is_alive') }}</ElCheckbox
              >
            </td>
          </tr>
          <tr>
            <th scope="col"
              ><div>{{ $t('asset') }}</div></th
            >
            <td>
              <select v-model="search_params.asset_seq">
                <option
                  v-for="{ seq, title } in [
                    { seq: undefined, title: $t('admin.total') },
                    ...assets,
                  ]"
                  :key="seq"
                  :value="seq"
                  >{{ title }}</option
                >
              </select>
            </td>
            <th scope="col"
              ><div>{{ $t('admin.sort') }}</div></th
            >
            <td>
              <select v-model="search_params.sort_order">
                <option
                  v-for="val in [
                    [undefined, $t('admin.regdate')],
                    ['balance', $t('wallet.balance')],
                    ['holding_balance', $t('wallet.hold_balance')],
                  ]"
                  :key="val[1]"
                  :value="val[0]"
                  >{{ val[1] }}</option
                >
              </select>
              <select v-model="search_params.order_type">
                <option
                  v-for="val in [
                    [undefined, $t('admin.desc')],
                    ['asc', $t('admin.asc')],
                  ]"
                  :key="val[1]"
                  :value="val[0]"
                  >{{ val[1] }}</option
                >
              </select>
            </td>
          </tr>
          <tr>
            <th colspan="4" :class="$style.button">
              <a
                href="#"
                :class="[$style.button, $style.red]"
                @click.prevent="clearAndSearch"
                ><span>{{ $t('admin.reset') }}</span></a
              >
              &nbsp;
              <span :class="[$style.button, $style.black]"
                ><input type="submit" :value="$t('admin.submit')"
              /></span>
            </th>
          </tr>
        </tbody>
      </table>
    </form>
    <h3 :class="$style.xeAdmin">{{ $t('wallet.wallet_list') }}</h3>
    <fieldset :class="$style.clearfix">
      <div :class="$style.fl">
        <a href="#" :class="[$style.button]" @click.prevent="handleExportExcel"
          ><span>{{ $t('admin.excel_download') }}</span></a
        >
      </div>
      <div :class="$style.fr">
        <a
          v-if="$route.params.member_seq"
          href="#"
          :class="[$style.button, $style.green]"
          @click.prevent="handleCreateAccount"
          ><span>{{ $t('admin.new_account') }}</span></a
        >
      </div>
    </fieldset>
    <table cellspacing="0" cellpadding="0" :class="$style.colTable">
      <caption v-if="!loading"
        >Total {{ total_count }}, Page {{ page }}/{{ total_page }}</caption
      >
      <thead>
        <template>
          <th scope="col" rowspan="2"
            ><div
              ><a href="#">{{ $t('admin.num') }}</a></div
            ></th
          >
          <template v-if="!$route.params.member_seq">
            <th scope="col" :class="$style.quarter_wide"
              ><div>{{ $t('admin.user_id') }}</div></th
            >
            <th scope="col" :class="$style.quarter_wide"
              ><div>{{ $t('admin.user_name') }}</div></th
            >
          </template>
          <th scope="col"
            ><div>{{ $t('wallet.account_name') }}</div></th
          >
          <th colspan="2" scope="col"
            ><div>{{ $t('wallet.account_number') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('wallet.balance') }}</div></th
          >
          <th scope="col" rowspan="2"
            ><div>{{ $t('admin.status') }}</div></th
          >
          <th scope="col" rowspan="2"
            ><div>{{ $t('wallet.regdate') }}</div></th
          >
          <th scope="col" rowspan="2"><div>&nbsp;</div></th>
        </template>
        <tr>
          <template v-if="!$route.params.member_seq">
            <th scope="col" colspan="2" :class="$style.quarter_wide"
              ><div>{{ $t('admin.email_address') }}</div></th
            >
          </template>
          <th scope="col"
            ><div>{{ $t('admin.deposit_address') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('admin.asset_title') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('admin.modal_asset_code') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('wallet.hold_balance') }}</div></th
          >
        </tr>
      </thead>
      <tbody>
        <template v-for="(data, idx) in list">
          <tr :key="`1-${data.seq}`" :class="$style[`bg${idx % 2}`]">
            <td rowspan="2" :class="[$style.tCenter, $style.nowrap]">{{
              data._no
            }}</td>
            <template v-if="!$route.params.member_seq">
              <td :class="[$style.tCenter, $style.nowrap]">{{
                data.user_id
              }}</td>
              <td :class="[$style.tCenter, $style.nowrap]">{{
                data.user_name
              }}</td>
            </template>
            <td :class="$style.wide">{{ data.title }}</td>
            <td colspan="2" :class="[$style.tCenter, $style.nowrap]">{{
              data.account_number
            }}</td>
            <td :class="[$style.tRight, $style.nowrap]">{{ data.balance }}</td>
            <td rowspan="2" :class="[$style.tCenter, $style.nowrap]">{{
              fromPairs(constants.account.status)[data.status]
            }}</td>
            <td rowspan="2" :class="[$style.tCenter, $style.nowrap]">{{
              date('Y-m-d H:i:s', new Date(data.regdate))
            }}</td>
            <td rowspan="2" :class="[$style.tRight, $style.nowrap]">
              <a
                v-if="data.status !== 'OPEN'"
                :class="[$style.button, $style.blue]"
                @click.prevent="handleAccountOpen(data.seq)"
                ><span>{{ $t('admin.normal') }}</span></a
              >
              {{ '' }}
              <a
                v-if="data.status !== 'FRAUD'"
                :class="[$style.button, $style.red]"
                @click.prevent="handleAccountFraud(data.seq)"
                ><span>{{ $t('admin.fraud') }}</span></a
              >
              {{ '' }}
              <router-link
                :to="{
                  name: 'admin-account-catalog',
                  params: { account_seq: data.seq },
                }"
                :class="[$style.button, $style.gray]"
                @click.prevent
                ><span>{{ $t('admin.account_detail') }}</span></router-link
              >
            </td>
          </tr>
          <tr :key="`2-${data.seq}`" :class="$style[`bg${idx % 2}`]">
            <template v-if="!$route.params.member_seq">
              <td colspan="2" :class="[$style.nowrap]">{{
                data.email_address
              }}</td>
            </template>
            <td :class="$style.wide">
              {{ data.address }}
              <a
                v-if="data.address_seq"
                href="#"
                :class="[$style.button, $style.small]"
                @click.prevent="handleCopyPrivateKey(data.address_seq)"
                ><span>{{ $t('admin.private_key_copy') }}</span></a
              >
            </td>
            <td :class="[$style.tCenter, $style.nowrap]">{{
              data.asset_title
            }}</td>
            <td :class="[$style.tCenter, $style.nowrap]">{{ data.code }}</td>
            <td :class="[$style.tRight, $style.nowrap]">{{
              data.holding_balance
            }}</td>
          </tr>
        </template>
        <tr v-if="loading">
          <td
            :colspan="$route.params.member_seq ? 8 : 10"
            :class="$style.tCenter"
            >{{ $t('admin.loading') }}</td
          >
        </tr>
        <tr v-else-if="!list.length">
          <td
            :colspan="$route.params.member_seq ? 8 : 10"
            :class="$style.tCenter"
            >{{ $t('admin.no_search_account') }}</td
          >
        </tr>
      </tbody>
    </table>
    <PageNavigation
      v-if="!loading"
      :total_count="total_count"
      :total_page="page_navigation.total_page"
      :cur_page="page"
      :page_count="page_navigation.page_count"
    ></PageNavigation>
  </div>
</template>

<style lang="scss" module>
@import '@src/design/default.scss';
@import '@src/design/admin.scss';
@import '@src/design/font.scss';
@import '@src/design/button.scss';

.colTable th,
.colTable td {
  vertical-align: middle;
}
</style>
